import type { JSX, MouseEvent } from 'react';
import {
  CHART_LANE_DIMMED_OPACITY,
  CHART_LANE_LINE_STROKE_WIDTH,
  DISEASE_BURDEN_LOD,
} from '@/constants/PatientDashboard/patientTimeline';
import type {
  IChartLaneLogScale,
  IDiseaseBurdenPoint,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { toChartLanePolylinePoints, yLogPct } from './ChartLane.util';

const HOVER_TARGET_STROKE_WIDTH = 14;

interface IProps {
  seriesKey: string;
  points: IDiseaseBurdenPoint[];
  colour: string;
  scale: IChartLaneLogScale;
  xPct: (date: string) => number;
  isHovered: boolean;
  isDimmed: boolean;
  onSeriesEnter: (event: MouseEvent<SVGPolylineElement>) => void;
  onSeriesLeave: () => void;
}

export default function BurdenSeriesLines({
  seriesKey,
  points,
  colour,
  scale,
  xPct,
  isHovered,
  isDimmed,
  onSeriesEnter,
  onSeriesLeave,
}: IProps): JSX.Element {
  const purityPoints = toChartLanePolylinePoints(
    points,
    (point) => xPct(point.date),
    (point) => yLogPct(point.purity, scale),
  );

  return (
    <g opacity={isDimmed ? CHART_LANE_DIMMED_OPACITY : undefined}>
      <polyline
        points={toChartLanePolylinePoints(
          points,
          (point) => xPct(point.date),
          (point) => yLogPct(point.lod, scale),
        )}
        fill="none"
        stroke={isHovered ? DISEASE_BURDEN_LOD.hoverColour : DISEASE_BURDEN_LOD.colour}
        strokeWidth={DISEASE_BURDEN_LOD.strokeWidth}
        strokeDasharray={DISEASE_BURDEN_LOD.dashArray}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <polyline
        points={purityPoints}
        fill="none"
        stroke={colour}
        strokeWidth={CHART_LANE_LINE_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <polyline
        points={purityPoints}
        fill="none"
        stroke="transparent"
        strokeWidth={HOVER_TARGET_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        pointerEvents="stroke"
        data-series={seriesKey}
        onMouseEnter={onSeriesEnter}
        onMouseLeave={onSeriesLeave}
      />
    </g>
  );
}
